/**
 * canvasBridge — pont entre les `canvas_actions_request` envoyés par l'agent
 * (`WebSocketPool`) et le **Document API** de SuperDoc.
 *
 * > Plus une seule ligne de ce module ne touche l'état ProseMirror ni les
 * > commandes Tiptap, hors les deux verbes d'ÉTAT D'INTERFACE nommés plus bas
 * > (#979, 2026-08-14). `superdoc@2` rend `editor.commands` à `null` et ne
 * > publie plus `editor.state` : tout ce qui passait par là mourait au bump.
 *
 * Capacités :
 *  - Read : `extractParagraphs`, `extractContextRange`, `extractParagraphFormat`,
 *    `extractDocumentStructure`, `extractSelection`. Corps SDM/1 via `doc.get()`
 *    et sélection via `doc.selection.current()` (#940) — voir
 *    `state/canvas-agent/lectureSdm.ts`.
 *  - Write : `applyActions(doc, actions, hoteUi?)` exécute un batch. Ordre
 *    déterministe via `sortActions` (intraParagraph → commentOps → indexed DESC
 *    → noOp) — voir doc de la fonction pour la sémantique initial-state.
 *
 * Surfaces d'écriture du Document API employées :
 *  - structure : `doc.insert` (HTML), `doc.blocks.delete`, `doc.replace`
 *  - style de paragraphe : `doc.styles.paragraph.setStyle`,
 *    `doc.format.paragraph.setAlignment`
 *  - marques de run : `doc.format.apply({inline})` — un seul patch par
 *    occurrence, `null` pour retirer une propriété
 *  - ciblage intra-paragraphe : `doc.query.match` borné au bloc (`within`), qui
 *    remplace `editor.commands.search()` et tout calcul de position ProseMirror
 *  - commentaires : `doc.comments.create/patch/delete`
 *
 * ⚠️ DEUX verbes passent par l'INTERFACE et non par le document —
 * `navigate_to_paragraph` et `set_active_comment`, voir `HoteUiCanvas`. Ce
 * n'est plus « faute d'équivalent public » (c'est ce que #979 avait conclu, et
 * ce que #980/T16 a réfuté le 2026-08-15) : ils ont une surface publique, elle
 * s'appelle `superdoc.ui`, et elle vit sur l'INSTANCE — `doc.*` décrit le
 * document, jamais la vue.
 */

import type {
  CanvasAction,
  CanvasActionResult,
  CanvasParagraphSummary,
  CanvasSelectionSummary,
} from '@hector/shared-types';
import { HECTOR_HEADING_STYLE_LEVELS, HECTOR_STYLE_IDS_PRESET } from '@hector/shared-types';
import { lireLeCorpsSdm, lireLesParagraphes, type ParagrapheSdm } from './canvas-agent/lectureSdm';
import { lireLaRevisionDuDocument, muterSousFences, revisionCourante, type RevisionDeDocument } from './canvas-agent/fencesDeMutation';
import type { CanvasActionContext } from './canvas-agent/textActionExecutors';
import type { SuperDocDocumentApi } from './canvas-agent/superDocDocumentApi';
import type { DocumentApiCreationDeTable } from '../editor/canvasTableCreation';

// ============================================================================
// Types du contrat wire : SOURCE UNIQUE @hector/shared-types/canvas
// (2026-07-03). Ré-exportés pour les consommateurs frontend historiques du
// bridge. Les payloads inbound sont validés par CanvasActionSchema dans
// WebSocketPool AVANT d'atteindre applyActions.
// ============================================================================

export type { CanvasAction, CanvasActionResult, CanvasParagraphSummary, CanvasSelectionSummary };

// ============================================================================
// HELPERS
// ============================================================================

const ALIGN_VALUES = new Set(['left', 'center', 'right', 'justify']);
/**
 * Couleurs de surlignage, en **noms OOXML** (`w:highlight w:val`).
 *
 * ⚠️ CHANGEMENT D'UNITÉ, et il est load-bearing : la commande Tiptap
 * `setHighlight(color)` prenait un hex de palette Tailwind (`#fef08a`), mais
 * `InlineRunPatch.highlight` alimente `w:highlight`, dont le vocabulaire est
 * une énumération fermée de noms (ECMA-376 : `yellow`, `magenta`, `green`…) —
 * pas une couleur libre. C'est aussi ce que la lecture SDM relit
 * (`lectureSdm.estSurligne`, qui traite `'none'` comme un surlignage retiré).
 * Les trois clés du contrat wire (`yellow` | `pink` | `green`) se projettent
 * donc sur les trois noms OOXML les plus proches ; `pink` devient `magenta`,
 * seul rose de l'énumération.
 */
const HIGHLIGHT_OOXML: Record<string, string> = {
  yellow: 'yellow',
  pink: 'magenta',
  green: 'green',
};
/**
 * Ce que `clear_formatting` retire, une propriété par appel.
 *
 * Le périmètre est celui d'avant : les marques Hector plus les propriétés de
 * run que `apply_font` pose (police, taille, couleur). Le type de nœud n'est
 * pas touché — un titre nettoyé reste un titre.
 */
const PROPRIETES_A_RETIRER: ReadonlyArray<Record<string, null>> = [
  { bold: null },
  { italic: null },
  { underline: null },
  { strike: null },
  { highlight: null },
  { color: null },
  { fontFamily: null },
  { fontSize: null },
];
// Map partagée (Hector-H1..6) + alias minuscules h1..h6 utilisés par les
// styleKey issus du streaming HTML (blocksToHtml) — alias LOCAUX au bridge,
// jamais exposés sur le wire (le contrat shared-types ne connaît que Hector-*).
const HEADING_STYLE_LEVELS: Record<string, number> = {
  ...HECTOR_HEADING_STYLE_LEVELS,
  h1: 1, h2: 2, h3: 3, h4: 4, h5: 5, h6: 6,
};

// ============================================================================
// READ (extract*)
// ============================================================================

/**
 * Style Hector d'un paragraphe SDM : les titres se nomment par leur niveau,
 * tout le reste par sa référence de style Word, `Hector-Normal` par défaut.
 *
 * Inverse de `setHeading({level})` pour les inserts streaming HTML — même
 * contrat qu'avant le portage, où le style venait de `node.attrs.styleId`.
 */
function styleKeyDuParagraphe(paragraphe: ParagrapheSdm): string {
  if (paragraphe.niveauDeTitre !== undefined) return `Hector-H${paragraphe.niveauDeTitre}`;
  return paragraphe.styleRef ?? 'Hector-Normal';
}

/**
 * Un item de `doc.comments.list()`, réduit à ce que la relecture consomme.
 * Source : `document-api/src/comments/comments.types.d.ts`
 * (`DiscoveryItem<CommentDomain>`, le item portant `id` + les champs du domaine).
 */
interface SuperDocCommentItem {
  id: string;
  commentId?: string;
  text?: string;
  isInternal?: boolean;
  status: 'active' | 'resolved' | string;
  anchoredText?: string;
}

/**
 * Index des commentaires par bloc d'ancrage.
 *
 * Remplace le walk des marks `commentMark` : `CommentInfo.target` est un
 * `TextTarget`, dont le premier segment porte le `blockId` — même information,
 * lue par l'API publique plutôt que par les marks ProseMirror. Best-effort :
 * si la liste échoue (DOCX corrompu, extension commentaires désactivée pour un
 * cabinet), on continue sans commentaires plutôt que de priver l'agent de
 * toute la lecture du document.
 */
async function lireLesCommentairesParBloc(
  doc: SuperDocDocumentApi,
): Promise<Map<string, NonNullable<CanvasParagraphSummary['comments']>>> {
  const parBloc = new Map<string, NonNullable<CanvasParagraphSummary['comments']>>();
  if (typeof doc.comments?.list !== 'function') return parBloc;
  let listed: unknown;
  try {
    listed = await doc.comments.list({});
  } catch {
    return parBloc;
  }
  if (typeof listed !== 'object' || listed === null) return parBloc;
  const items = (listed as { items?: unknown }).items;
  if (!Array.isArray(items)) return parBloc;
  for (const raw of items) {
    if (typeof raw !== 'object' || raw === null) continue;
    const item = raw as SuperDocCommentItem & { target?: { segments?: Array<{ blockId?: unknown }> } };
    const commentId = item.commentId ?? item.id;
    const blockId = item.target?.segments?.[0]?.blockId;
    if (typeof commentId !== 'string' || typeof blockId !== 'string') continue;
    const entries = parBloc.get(blockId) ?? [];
    entries.push({
      commentId,
      text: item.anchoredText ?? '',
      commentText: item.text ?? '',
      ...(item.isInternal !== undefined ? { isInternal: item.isInternal } : {}),
      ...(item.status ? { status: item.status } : {}),
    });
    parBloc.set(blockId, entries);
  }
  return parBloc;
}

export async function extractParagraphs(doc: SuperDocDocumentApi): Promise<CanvasParagraphSummary[]> {
  const paragraphes = lireLesParagraphes(await lireLeCorpsSdm(doc));
  const commentairesParBloc = await lireLesCommentairesParBloc(doc);

  return paragraphes.map((paragraphe) => {
    const { marques } = paragraphe;
    const commentEntries = paragraphe.noeud.id ? commentairesParBloc.get(paragraphe.noeud.id) : undefined;
    return {
      index: paragraphe.index,
      styleKey: styleKeyDuParagraphe(paragraphe),
      text: paragraphe.texte,
      ...(marques.hasBold ? { hasBold: true } : {}),
      ...(marques.hasItalic ? { hasItalic: true } : {}),
      ...(marques.hasUnderline ? { hasUnderline: true } : {}),
      ...(marques.hasHighlight ? { hasHighlight: true } : {}),
      ...(paragraphe.alignement ? { align: paragraphe.alignement } : {}),
      ...(commentEntries && commentEntries.length > 0 ? { comments: commentEntries } : {}),
    };
  });
}

export async function extractContextRange(
  doc: SuperDocDocumentApi,
  start: number,
  end: number,
): Promise<CanvasParagraphSummary[]> {
  const all = await extractParagraphs(doc);
  const lo = Math.max(0, Math.min(start, end));
  const hi = Math.min(all.length - 1, Math.max(start, end));
  if (all.length === 0) return [];
  return all.slice(lo, hi + 1);
}

export async function extractParagraphFormat(
  doc: SuperDocDocumentApi,
  index: number,
): Promise<{
  index: number;
  styleKey: string;
  text: string;
  hasBold: boolean;
  hasItalic: boolean;
  hasUnderline: boolean;
  allBold: boolean;
  allItalic: boolean;
  allUnderline: boolean;
  align?: string;
} | null> {
  const target = lireLesParagraphes(await lireLeCorpsSdm(doc)).find((p) => p.index === index);
  if (!target) return null;

  const { runs } = target.marques;
  return {
    index,
    styleKey: styleKeyDuParagraphe(target),
    text: target.texte,
    hasBold: target.marques.hasBold,
    hasItalic: target.marques.hasItalic,
    hasUnderline: target.marques.hasUnderline,
    allBold: runs.length > 0 && runs.every((r) => r.bold),
    allItalic: runs.length > 0 && runs.every((r) => r.italic),
    allUnderline: runs.length > 0 && runs.every((r) => r.underline),
    ...(target.alignement ? { align: target.alignement } : {}),
  };
}

export async function extractDocumentStructure(
  doc: SuperDocDocumentApi,
): Promise<Array<{ level: number; title: string; paragraph_index: number }>> {
  const out: Array<{ level: number; title: string; paragraph_index: number }> = [];
  for (const paragraphe of lireLesParagraphes(await lireLeCorpsSdm(doc))) {
    // Un titre porte son niveau ; un paragraphe ne compte comme titre que si
    // son style Hector en désigne un (`Hector-H1`…), ce qui couvre les actes
    // importés où le niveau vit dans le style et pas dans le type de nœud.
    const level = paragraphe.niveauDeTitre ?? HEADING_STYLE_LEVELS[styleKeyDuParagraphe(paragraphe)];
    if (typeof level !== 'number') continue;
    const title = paragraphe.texte.trim();
    if (title) out.push({ level, title, paragraph_index: paragraphe.index });
  }
  return out;
}

export async function extractSelection(doc: SuperDocDocumentApi): Promise<CanvasSelectionSummary> {
  if (typeof doc.selection?.current !== 'function') return { has_selection: false };
  const selection = await doc.selection.current({ includeText: true });
  if (typeof selection !== 'object' || selection === null) return { has_selection: false };
  const info = selection as {
    empty?: boolean;
    target?: { segments?: Array<{ blockId?: unknown }> } | null;
    text?: unknown;
  };
  const segments = info.target?.segments;
  if (!Array.isArray(segments) || segments.length === 0) return { has_selection: false };

  // `SelectionInfo` adresse les blocs par identité, pas par position : l'index
  // visible se retrouve en projetant les mêmes paragraphes que le reste de la
  // surface de lecture. Une sélection posée dans une table ne correspond à
  // aucun index visible — le contrat `paragraph_index` n'y adresse rien.
  const indexParBloc = new Map<string, number>();
  for (const paragraphe of lireLesParagraphes(await lireLeCorpsSdm(doc))) {
    if (paragraphe.noeud.id) indexParBloc.set(paragraphe.noeud.id, paragraphe.index);
  }
  const indices = segments
    .map((segment) => (typeof segment?.blockId === 'string' ? indexParBloc.get(segment.blockId) : undefined))
    .filter((index): index is number => typeof index === 'number');
  if (indices.length === 0) return { has_selection: false };

  const paragraphMin = Math.min(...indices);
  const paragraphMax = Math.max(...indices);
  // Curseur posé sans sélection : on rend quand même l'index courant pour que
  // l'agent puisse cibler le bloc actif — comportement inchangé.
  if (info.empty === true) return { has_selection: true, paragraph_index: paragraphMin };
  const text = typeof info.text === 'string' && info.text.length > 0 ? info.text : undefined;
  return {
    has_selection: true,
    paragraph_index: paragraphMin,
    ...(paragraphMax !== paragraphMin ? { end_paragraph_index: paragraphMax } : {}),
    ...(text ? { text } : {}),
  };
}

// ============================================================================
// WRITE (sortActions + applyActions) — Document API depuis #979
// ============================================================================

/**
 * Sous-ensemble d'ÉCRITURE du Document API utilisé par le pont.
 *
 * Tout est optionnel : une version du paquet qui n'exposerait pas une surface
 * doit produire un refus nommé par action, jamais un `TypeError` qui emporterait
 * le lot entier.
 *
 * ⚠️ **`create` et `tables` sont déclarées ici depuis #980, et leur ABSENCE
 * était la cause documentée d'un angle mort qui a coûté le gel de la bascule
 * V2.** Ce type est le vocabulaire d'écriture du canvas : ce qu'il ne nomme pas
 * n'apparaît pas en autocomplétion, n'apparaît pas en revue, et finit par
 * passer pour inexistant. `doc.create.table` — l'analogue exact de
 * l'`insertTable(rows, cols, position)` d'Office.js que l'add-in Word utilise en
 * production — est au catalogue public de `superdoc` depuis le début et n'a
 * jamais été appelée : trois enquêtes successives ont cherché une opération de
 * création dans `blocks.*`, où il n'y en a jamais eu. Elles sont importées de
 * `canvasTableCreation` plutôt que redéclarées, pour qu'il n'existe qu'UNE
 * description de ces opérations dans le canvas.
 */
export interface CanvasWriteDocumentApi
  extends SuperDocDocumentApi,
  // `query` et `styles` sont exclus de l'héritage : le pont les déclare déjà
  // (l'un pour la lecture, l'autre plus bas), et TypeScript exige des membres
  // hérités IDENTIQUES, pas seulement compatibles. Les familles qui manquaient
  // vraiment — `create` et `tables` — sont, elles, reprises telles quelles.
  Omit<DocumentApiCreationDeTable, 'query' | 'styles'> {
  insert?: ProviderMethod;
  replace?: ProviderMethod;
  blocks?: SuperDocDocumentApi['blocks'] & { delete?: ProviderMethod };
  format?: {
    apply?: ProviderMethod;
    paragraph?: { setAlignment?: ProviderMethod; setFlowOptions?: ProviderMethod; setBorder?: ProviderMethod };
  };
  styles?: { paragraph?: { setStyle?: ProviderMethod } };
  comments?: SuperDocDocumentApi['comments'] & {
    create?: ProviderMethod;
    patch?: ProviderMethod;
    delete?: ProviderMethod;
  };
}

type ProviderMethod = (input?: unknown, options?: unknown) => unknown;

/**
 * Contexte d'exécution du pont — **le même que celui des exécuteurs canoniques**
 * (#939), avec le Document API élargi aux surfaces d'écriture du pont.
 *
 * C'est lui qui apporte les trois gardes que le pont n'avait pas : le
 * vérificateur d'époque, la fenêtre de mutation par appel (`mutate`) et la
 * révision attendue.
 */
export interface CanvasBridgeContext extends CanvasActionContext {
  doc: CanvasActionContext['doc'] & CanvasWriteDocumentApi;
  /** Racine DOM du scope exact, utilisée uniquement par le repli de navigation. */
  queryRoot?: ParentNode;
}

/**
 * Les surfaces qui acceptent `changeMode:'tracked'` — **mesuré**, jamais déduit
 * des typages. Établie le 2026-08-14 contre `superdoc@1.44.1` (#979 / I2),
 * **RE-MESURÉE le 2026-08-15 contre `superdoc@2.6.0`** (#980 / T15, spec
 * `tests/ws-mocked/mesure-table-largeur.spec.ts`, bloc `[MESURE] mesure3-*`).
 *
 * ## Ce que la V2 a CHANGÉ — deux verdicts sur trois se sont inversés
 *
 * **1. `doc.replace` en tracked N'ENTRELACE PLUS.** C'était la seule raison de
 * le forcer en `direct` : en 1.44.1, « le contrat » → « JEAN DUPONT » donnait
 * `leJEAN contratDUPONT` dans le document peint. En 2.6.0, le même appel produit
 * **une révision de remplacement propre et appariée** —
 * `{type:'replacement', subtype:'text-replacement', insertedText:'REMPLACEMENT',
 * deletedText:'Le'}` — et le texte inséré est contigu à l'écran.
 *
 * ⚠️ **La valeur reste pourtant `direct`, et c'est délibéré** : la contrainte
 * technique a disparu, donc le choix redevient un **arbitrage produit** (« un
 * remplacement demandé par l'avocat doit-il apparaître en révision suivie ? »),
 * qui n'appartient pas à ce ticket. Ne pas basculer sans cette décision — mais
 * ne pas non plus laisser croire que c'est encore le runtime qui l'impose.
 *
 * **2. `doc.insert` (type `html`) N'EST PLUS REFUSÉ en tracked… et ne suit
 * toujours rien.** Le message `Tracked mode is not supported for type: 'html'`
 * a disparu : l'appel rend `success:true`. Mais `trackChanges.list` rend
 * **`total: 0`** après — ni en `tracked` explicite, ni en mode par défaut sous
 * une fenêtre `suggesting`. **La note historique « sous suggesting, une
 * insertion HTML directe produit bien une révision suivie (0 → 1) » est donc
 * FAUSSE en V2** : elle ne produit plus rien. Un refus nommé est devenu une
 * acceptation muette — le pire des deux mondes pour qui lit un receipt.
 *
 * **3. Qui signe désormais.** `author: "Avocat"`,
 * `authorEmail: "avocat@hector.local"` : les révisions portent l'identité de
 * l'avocat connecté, l'identité agent ayant été retirée (commit `594d6fc4c`).
 * Conséquence produit actée, vérifiée à l'exécution.
 *
 * ⚠️ **Non re-mesurées en V2** : `doc.blocks.delete`, `doc.format.apply`,
 * `styles.paragraph.setStyle` et `format.paragraph.setAlignment`. Leurs valeurs
 * ci-dessous datent de 1.44.1 — les deux inversions ci-dessus interdisent de
 * les supposer stables.
 *
 * Les opérations de commentaire n'ont pas de `changeMode` du tout : leur contrat
 * est `RevisionGuardOptions` (révision seule), pas `MutationOptions`.
 *
 * ## ⚠️ Ce que cette carte ne couvre PAS : la création de table
 *
 * `create.table` et `tables.setCellText` n'y figurent pas, et c'est voulu :
 * elles ne passent pas par le pont. Elles s'exécutent **hors de toute fenêtre de
 * suggestion**, en `direct` écrit au site (`editor/canvasTableCreation.ts`),
 * parce que `setCellText` est annoncée `tracked:false` aux capabilities — une
 * table serait suivie et son contenu non — et parce que l'écriture d'auteur
 * d'un acte généré n'est pas une écriture suivie (règle produit du 2026-08-15).
 */
const MODE_PAR_SURFACE: Record<string, 'tracked' | 'direct'> = {
  'doc.blocks.delete': 'tracked',
  'doc.format.apply': 'tracked',
  // ⚠️ `doc.replace` reste direct — mais PLUS pour la raison d'origine.
  // 2026-08-14 (1.44.1) : un remplacement suivi entrelaçait l'ancien et le
  // nouveau texte caractère par caractère (`leJEAN contratDUPONT`), donc la
  // révision suivie coûtait la lisibilité du résultat.
  // 2026-08-15 (2.6.0, re-mesuré) : ce défaut a DISPARU — la révision produite
  // est un remplacement apparié propre, texte inséré contigu à l'écran. La
  // contrainte technique est levée ; ce qui reste est un arbitrage produit,
  // délibérément non tranché ici. Voir le doc de la carte.
  'doc.replace': 'direct',
  // ⚠️ 2026-08-15 : le refus nommé de 1.44.1 (« Tracked mode is not supported
  // for type: 'html' ») a disparu — l'appel est ACCEPTÉ en tracked, et ne
  // produit aucune révision (compteur 0). Rester en direct évite de demander un
  // mode que le provider accepte sans l'honorer.
  'doc.insert': 'direct',
  'doc.styles.paragraph.setStyle': 'direct',
  'doc.format.paragraph.setAlignment': 'direct',
  // Retrait du saut de page hérité (#998) : `setFlowOptions` ne supporte pas le
  // mode suivi, et il n'y aurait rien à faire réviser — ce geste défait un
  // héritage accidentel, il n'écrit rien que l'avocat ait à accepter.
  'doc.format.paragraph.setFlowOptions': 'direct',
};

/** Adresse d'un bloc, forme attendue par toutes les surfaces de mutation. */
type AdresseDeBloc = { kind: 'block'; nodeType: string; nodeId: string };

/** Un bloc adressable par `paragraph_index`. */
interface BlocAdressable {
  /** Index VISIBLE — tables et sections sautées, comme le walk d'avant. */
  index: number;
  nodeId: string;
  nodeType: string;
  /**
   * Niveau 1..6 d'un titre (`BlockListEntry.headingLevel`). Sans lui, un
   * `replace_paragraph` sans style explicite rétrograderait tout titre en H1 —
   * la hiérarchie du volet Navigation de Word y passerait.
   */
  headingLevel?: number;
  /** Handle de mutation sur le texte entier du bloc, quand le provider en rend un. */
  ref?: string;
  text: string;
}

/** Une occurrence de texte dans un bloc, prête à muter. */
interface OccurrenceDeTexte {
  /** Cible de sélection canonique — `doc.replace`, `doc.format.apply` la prennent telle quelle. */
  target: unknown;
  /** Adresse de texte `{blockId, range}` — c'est ce que `comments.create` exige. */
  blockId: string;
  range: { start: number; end: number };
}

const TAILLE_DE_PAGE_BLOCS = 250;
/** Blocs que `paragraph_index` adresse. Une table top-level est SAUTÉE. */
const TYPES_ADRESSABLES = new Set(['paragraph', 'heading']);

function estUnObjet(valeur: unknown): valeur is Record<string, unknown> {
  return typeof valeur === 'object' && valeur !== null;
}

function chaineNonVide(valeur: unknown): string | undefined {
  return typeof valeur === 'string' && valeur.length > 0 ? valeur : undefined;
}

/**
 * `{success:false}` explicite du provider — tout le reste compte comme accepté.
 *
 * ⚠️ `NO_OP` est un SUCCÈS : le provider le rend quand l'état demandé est déjà
 * celui du document (« format produced no change »). Retirer un gras d'un texte
 * qui n'en porte pas est un no-op, pas un échec — le compter comme tel ferait
 * échouer `clear_formatting` sur tout paragraphe déjà propre. Même lecture que
 * les exécuteurs canoniques (`styleActionExecutors.stepOutcome`).
 */
function echecDuProvider(resultat: unknown): string | undefined {
  if (!estUnObjet(resultat) || resultat.success !== false) return undefined;
  const failure = estUnObjet(resultat.failure) ? resultat.failure : {};
  const code = chaineNonVide(failure.code) ?? 'sans code';
  if (code === 'NO_OP') return undefined;
  const message = chaineNonVide(failure.message) ?? 'sans message';
  return `${code}: ${message}`;
}

/**
 * UN appel mutant du pont, **sous les trois gardes de #939**, et qui lève un
 * message LLM-lisible quand le provider refuse.
 *
 * Les trois gardes (époque ré-assertée juste avant, révision attendue portée par
 * l'opération, fenêtre de mutation refermée derrière) viennent de
 * `muterSousFences` — le même geste que les exécuteurs canoniques, pas une
 * seconde implémentation.
 *
 * ⚠️ **La révision attendue est RELUE à chaque appel** (`revisionCourante`), pas
 * capturée une fois pour le lot. Le pont n'en reçoit aucune de son demandeur —
 * son contrat wire ne porte ni `documentSessionId` ni `expectedRevision`, à la
 * différence de l'enveloppe canonique — donc la seule valeur juste est celle du
 * document à l'instant de l'appel. C'est ce qui fait échouer une mutation quand
 * l'avocat a tapé entre la lecture des index et l'écriture, au lieu de poser la
 * marque au mauvais endroit.
 *
 * @param surface - Clé de `MODE_PAR_SURFACE` : c'est elle qui décide `tracked`
 *   ou `direct`, d'après la mesure et non d'après une intention.
 */
async function muterEtExigerSucces(
  fence: FenceDAction,
  operation: string,
  surface: keyof typeof MODE_PAR_SURFACE | string,
  appel: (options: { changeMode: 'tracked' | 'direct'; expectedRevision: RevisionDeDocument }) => unknown,
): Promise<unknown> {
  const changeMode = MODE_PAR_SURFACE[surface] ?? 'direct';
  const options = { changeMode, expectedRevision: await fence.revisionAttendue() } as const;
  const resultat = await muterSousFences(fence.ctx, `${operation}:${surface}`, options, () => appel(options));
  const echec = echecDuProvider(resultat);
  if (echec !== undefined) throw new Error(`${operation} refusé par SuperDoc — ${echec}.`);
  fence.marquerMutation();
  return resultat;
}

/**
 * La révision attendue d'UNE action, et sa règle d'avancement.
 *
 * - **Première mutation** : la révision lue en même temps que les index. C'est
 *   elle qui refuse l'écriture si l'avocat a tapé entre-temps — le défaut que
 *   la fence existe pour fermer.
 * - **Mutations suivantes de la même action** (`replace_paragraph` = insertion +
 *   suppression, `clear_formatting` = une propriété par appel) : la révision
 *   RELUE, puisque la précédente vient de la faire avancer. Même règle que les
 *   exécuteurs canoniques (#939, garde (b)).
 */
interface FenceDAction {
  ctx: CanvasBridgeContext;
  revisionAttendue: () => Promise<RevisionDeDocument>;
  marquerMutation: () => void;
}

function ouvrirLaFence(ctx: CanvasBridgeContext, revisionDeLecture: RevisionDeDocument | undefined): FenceDAction {
  let aDejaMute = false;
  return {
    ctx,
    revisionAttendue: async () => (aDejaMute || revisionDeLecture === undefined
      ? revisionCourante(ctx, revisionDeLecture ?? ctx.expectedRevision ?? '')
      : revisionDeLecture),
    marquerMutation: () => { aDejaMute = true; },
  };
}

/**
 * Énumère les blocs adressables par `paragraph_index`.
 *
 * ⚠️ Le FILTRE est le contrat : seuls `paragraph` et `heading` comptent, et
 * l'index visible se recalcule à CHAQUE action. C'était déjà le cas avec le walk
 * ProseMirror (`editor.state.doc` était relu à chaque tour de boucle) : une
 * insertion faite plus tôt dans le lot décale les index suivants, et
 * `sortActions` compte là-dessus.
 */
async function listerBlocsAdressables(doc: CanvasWriteDocumentApi): Promise<BlocAdressable[]> {
  return (await lireLesBlocsEtLaRevision(doc)).blocs;
}

/**
 * La révision du document telle que le verrou optimiste l'entend.
 *
 * Un seul lecteur la rend : `lireLaRevisionDuDocument` (`doc.info()`, et son
 * repli quand `info` lève). `undefined` quand la surface est absente : le fence
 * retombe alors sur la relecture, jamais sur une valeur d'un autre espace.
 */
async function revisionDeLaLecture(doc: CanvasWriteDocumentApi): Promise<RevisionDeDocument | undefined> {
  return lireLaRevisionDuDocument(doc);
}

/**
 * Les blocs adressables ET la révision à laquelle ils ont été lus.
 *
 * ⚠️ **La révision de la LECTURE est ce qui fence la mutation**, pas celle de
 * l'instant où on l'exécute. Une action est décidée à partir d'index lus ; si
 * l'avocat tape entre cette lecture et l'écriture, la mutation doit être
 * refusée. Relire la révision juste avant de muter donnerait toujours une valeur
 * à jour — c'est-à-dire une fence qui ne refuse jamais rien.
 *
 * ⚠️ **Elle vient de `lireLaRevisionDuDocument`, jamais du `revision` de la
 * liste paginée ci-dessous** (#980, mesuré le 2026-08-15 sur 2.6.0). Les deux sont typées
 * `string` côté provider et vivent dans deux espaces différents : la seconde est
 * un identifiant de paquet de lecture (`sd-…:pkg:1`) que le verrou optimiste
 * refuse systématiquement — c'est ce qui a fait échouer TOUTE mutation agent
 * pendant la bascule. Voir `RevisionDeDocument`.
 *
 * ⚠️ **Elle est lue AVANT la liste, et l'ordre est délibéré.** La lister d'abord
 * laisserait passer un changement survenu pendant la pagination : la fence
 * porterait alors une valeur plus RÉCENTE que les index qu'elle protège, donc
 * elle laisserait écrire sur une lecture déjà périmée. Lire la révision en
 * premier fait échouer ce cas au lieu de le laisser passer.
 */
async function lireLesBlocsEtLaRevision(
  doc: CanvasWriteDocumentApi,
): Promise<{ blocs: BlocAdressable[]; revision: RevisionDeDocument | undefined }> {
  if (typeof doc.blocks?.list !== 'function') {
    throw new Error(
      'canvasBridge: `doc.blocks.list` indisponible sur cet éditeur SuperDoc — aucun '
      + 'paragraph_index ne peut être résolu.',
    );
  }
  const out: BlocAdressable[] = [];
  let index = 0;
  const revision = await revisionDeLaLecture(doc);
  for (let offset = 0; ; offset += TAILLE_DE_PAGE_BLOCS) {
    const page = await doc.blocks.list({ offset, limit: TAILLE_DE_PAGE_BLOCS, includeText: true });
    const entrees = estUnObjet(page) && Array.isArray(page.blocks) ? page.blocks : [];
    for (const brut of entrees) {
      if (!estUnObjet(brut)) continue;
      const nodeId = chaineNonVide(brut.nodeId);
      const nodeType = chaineNonVide(brut.nodeType);
      if (!nodeId || !nodeType || !TYPES_ADRESSABLES.has(nodeType)) continue;
      const niveau = brut.headingLevel;
      out.push({
        index: index++,
        nodeId,
        nodeType,
        ...(typeof niveau === 'number' && Number.isInteger(niveau) && niveau >= 1 && niveau <= 6
          ? { headingLevel: niveau }
          : {}),
        ...(chaineNonVide(brut.ref) === undefined ? {} : { ref: chaineNonVide(brut.ref)! }),
        text: typeof brut.text === 'string' ? brut.text : (chaineNonVide(brut.textPreview) ?? ''),
      });
    }
    if (entrees.length < TAILLE_DE_PAGE_BLOCS) return { blocs: out, revision };
  }
}

/**
 * Pose une suite de marques de run sur toutes les occurrences d'un texte, en
 * RE-RÉSOLVANT la cible avant chaque appel.
 *
 * ⚠️ C'est la relecture qui est load-bearing, et elle a été mesurée
 * (2026-08-14). Les cibles rendues par `query.match` sont **éphémères et
 * portées par une révision** — le provider le dit lui-même quand on en réutilise
 * une (`REVISION_MISMATCH — Text ref is ephemeral and revision-scoped. Re-run
 * query.match to obtain a fresh handle`). Or chaque `format.apply` fait avancer
 * la révision. Rejouer la même cible pour la marque suivante donne alors, selon
 * la surface, un refus explicite ou — pire — un `success:true` dont l'effet
 * ANNULE la marque précédente : demander gras + italique + souligné ne laissait
 * que le souligné, sans qu'aucun receipt ne le signale.
 *
 * Le découpage marque par marque est lui aussi mesuré : un `InlineRunPatch`
 * groupé n'en applique qu'une. La chain Tiptap enchaînait déjà une commande par
 * marque — c'est le même geste, avec la relecture que l'asynchronisme impose.
 */
async function poserLesMarques(
  fence: FenceDAction,
  bloc: BlocAdressable,
  motif: string,
  nombreDOccurrences: number,
  marques: ReadonlyArray<Record<string, unknown>>,
  operation: string,
): Promise<void> {
  const doc = fence.ctx.doc;
  for (let rang = 0; rang < nombreDOccurrences; rang++) {
    for (const inline of marques) {
      const fraiches = await trouverLesOccurrences(doc, bloc, motif);
      const cible = fraiches[rang];
      if (cible === undefined) {
        throw new Error(
          `${operation}: l'occurrence ${rang + 1} de « ${motif} » a disparu du paragraphe `
          + `${bloc.index} pendant l'application des marques — le document a changé sous l'action.`,
        );
      }
      await muterEtExigerSucces(fence, operation, 'doc.format.apply', (options) =>
        doc.format!.apply!({ target: cible.target, inline }, options));
    }
  }
}

/** Message d'erreur inchangé : le backend le relaie au modèle tel quel. */
function resoudreParagraphe(blocs: readonly BlocAdressable[], index: number): BlocAdressable {
  const bloc = blocs[index];
  if (bloc === undefined) {
    throw new Error(
      `paragraph_index=${index} hors limites — ${blocs.length} paragraphes top-level disponibles `
      + `(les tables / sections sont SKIP en PR2).`,
    );
  }
  return bloc;
}

function adresseDe(bloc: BlocAdressable): AdresseDeBloc {
  return { kind: 'block', nodeType: bloc.nodeType, nodeId: bloc.nodeId };
}

/**
 * Toutes les occurrences d'un texte DANS un bloc, dans l'ordre du document.
 *
 * Remplace `editor.commands.search()` + filtrage au range du paragraphe :
 * `query.match` borne nativement au bloc (`within`) et rend, par occurrence, la
 * cible de sélection canonique ET l'adresse de texte. Aucun calcul de position
 * ProseMirror ne subsiste — c'était la partie la plus fragile du pont.
 */
async function trouverLesOccurrences(
  doc: CanvasWriteDocumentApi,
  bloc: BlocAdressable,
  motif: string,
  options: { caseSensitive?: boolean } = {},
): Promise<OccurrenceDeTexte[]> {
  if (typeof doc.query?.match !== 'function') {
    throw new Error(
      'canvasBridge: `doc.query.match` indisponible sur cet éditeur SuperDoc — aucune recherche '
      + 'de texte intra-paragraphe n\'est possible.',
    );
  }
  // ⚠️ FORME RACCOURCIE, mesurée : `query.match` accepte le sélecteur À PLAT
  // (`{type:'text', pattern, within}`), pas l'enveloppe `{select:{…}}` que
  // laissent croire les typages — celle-ci rend `MATCH_NOT_FOUND` sur un texte
  // pourtant présent (mesuré sur le vrai SuperDoc, 2026-08-14). C'est la forme
  // qu'emploient déjà les exécuteurs canoniques (`textActionExecutors`), et la
  // seule vérifiée en live.
  const resultat = await doc.query.match({
    type: 'text',
    pattern: motif,
    caseSensitive: options.caseSensitive ?? false,
    within: adresseDe(bloc),
  });
  const items = estUnObjet(resultat) && Array.isArray(resultat.items) ? resultat.items : [];
  return items.flatMap((item) => {
    if (!estUnObjet(item)) return [];
    const premierBloc = Array.isArray(item.blocks) ? item.blocks[0] : undefined;
    if (!estUnObjet(premierBloc)) return [];
    const blockId = chaineNonVide(premierBloc.blockId);
    const range = estUnObjet(premierBloc.range) ? premierBloc.range : undefined;
    if (!blockId || !range || typeof range.start !== 'number' || typeof range.end !== 'number') return [];
    return [{ target: item.target, blockId, range: { start: range.start, end: range.end } }];
  });
}

/**
 * Trie les actions pour exécution sûre (copié verbatim depuis canvasBridge.ts —
 * la sémantique initial-state des batches backend dépend de cet ordre exact).
 *
 * 1. `intraParagraph` (search_replace, apply_text_format, highlight_range,
 *    insert_comment) — opèrent INTRA paragraphe. EN PREMIER pour que leurs
 *    `paragraph_index` réfèrent l'état INITIAL avant que les inserts/deletes
 *    structurels ne décalent les indices.
 * 2. `indexed` (insert/delete/replace/apply_style/apply_font/clear_formatting/
 *    set_alignment) — peuvent décaler les indices. Triées DÉCROISSANT pour que
 *    les hauts indices soient traités d'abord — les bas restent valides après.
 * 3. `noOp` (navigate_to_paragraph) — UI only. En dernier.
 */
function sortActions(actions: CanvasAction[]): CanvasAction[] {
  const indexed: CanvasAction[] = [];
  const intraParagraph: CanvasAction[] = [];
  const commentOps: CanvasAction[] = [];
  const noOp: CanvasAction[] = [];
  for (const a of actions) {
    if (
      a.kind === 'search_replace' ||
      a.kind === 'apply_text_format' ||
      a.kind === 'highlight_range' ||
      a.kind === 'insert_comment'
    ) {
      intraParagraph.push(a);
    } else if (
      a.kind === 'resolve_comment' ||
      a.kind === 'reopen_comment' ||
      a.kind === 'delete_comment' ||
      a.kind === 'set_active_comment'
    ) {
      // PR3 — opérations sur commentaires existants. N'affectent pas les
      // paragraph_index (le commentMark n'altère pas le node count), donc
      // ordre relatif libre. On les groupe en bucket dédié exécuté APRÈS
      // intraParagraph (besoin de l'état post-replace si l'agent a fait
      // search_replace dans le même batch) mais AVANT les indexed (pour que
      // delete_comment + delete_paragraph qui contient l'ancre ne déclenche
      // pas une erreur d'ancre orpheline).
      commentOps.push(a);
    } else if (a.kind === 'navigate_to_paragraph') {
      noOp.push(a);
    } else {
      indexed.push(a);
    }
  }
  const idxKey = (x: CanvasAction): number => {
    if (x.kind === 'insert_paragraph') return x.after_index;
    if (x.kind === 'apply_font' || x.kind === 'clear_formatting' || x.kind === 'set_alignment') {
      return x.paragraph_index;
    }
    return (x as { index: number }).index;
  };
  indexed.sort((a, b) => idxKey(b) - idxKey(a));
  return [...intraParagraph, ...commentOps, ...indexed, ...noOp];
}

/**
 * L'adresse du bloc que l'insertion vient de créer, lue dans le receipt.
 *
 * Best-effort : un provider qui ne publierait pas ses effets ne doit pas faire
 * échouer une insertion qui a réussi. Même lecture que
 * `canvasDocumentWrite.blocInsere`, sur l'autre chemin d'écriture.
 */
function blocRenduParLInsertion(receipt: unknown): AdresseDeBloc | null {
  if (!estUnObjet(receipt)) return null;
  const effects = estUnObjet(receipt.effects) ? receipt.effects : undefined;
  const premier = Array.isArray(effects?.insertedBlocks) ? effects.insertedBlocks[0] : undefined;
  const cible = estUnObjet(premier) && estUnObjet(premier.target) ? premier.target : undefined;
  const nodeId = cible?.nodeId;
  const nodeType = cible?.nodeType;
  if (typeof nodeId !== 'string' || nodeId.length === 0 || typeof nodeType !== 'string') return null;
  return { kind: 'block', nodeType, nodeId };
}

/**
 * Retire le saut de page qu'un paragraphe fraîchement inséré a HÉRITÉ de son
 * voisin.
 *
 * ⚠️ **Sans ce geste, le paragraphe est invisible à l'écran** — défaut mesuré le
 * 2026-08-15 ([#998]) et diagnostiqué par l'EXPORT, seul juge à l'avoir montré :
 * `word/document.xml` portait DEUX `<w:pageBreakBefore/>` alors qu'un seul avait
 * été posé. Le provider recopie les propriétés de paragraphe du voisin sur le
 * bloc inséré, saut de page compris ; le peintre V2 ne rend alors pas un
 * paragraphe qui ouvre lui-même une page en fin de document, et affiche une page
 * VIDE à sa place. Le texte est bien dans le modèle et dans le `.docx` — c'est
 * un mensonge d'affichage, et rien ne l'annonce : le receipt est vert et l'agent
 * conclut au succès.
 *
 * ⚠️ **Ce retrait ne s'applique QU'AU PONT, et c'est délibéré.** `insert_paragraph`
 * n'a aucune notion de saut de page : personne, sur ce chemin, n'en a demandé un.
 * Les sauts VOULUS sont posés ailleurs — `normaliserBlocsInseres`
 * (`editor/canvasStructuralNormalization.ts`) les applique sur le chemin de
 * génération, d'après les marqueurs du parseur partagé (PLAISE, BORDEREAU,
 * PIÈCES JOINTES). Ce chemin-là ne passe pas ici et n'est pas touché. Ne jamais
 * généraliser ce retrait à `replace_paragraph` : y hériter de la mise en forme du
 * paragraphe remplacé est au contraire le comportement attendu.
 *
 * Best-effort : un échec est un défaut de rendu, jamais une perte de texte — le
 * paragraphe est déjà dans le document.
 */
async function retirerLeSautHerite(
  fence: FenceDAction,
  doc: CanvasWriteDocumentApi,
  receipt: unknown,
): Promise<void> {
  const cible = blocRenduParLInsertion(receipt);
  if (cible === null || typeof doc.format?.paragraph?.setFlowOptions !== 'function') return;
  try {
    await muterEtExigerSucces(fence, 'insert_paragraph', 'doc.format.paragraph.setFlowOptions', (options) =>
      doc.format!.paragraph!.setFlowOptions!({ target: cible, pageBreakBefore: false }, options));
  } catch (erreur) {
    // eslint-disable-next-line no-console
    console.warn(
      `[canvasBridge] saut de page hérité NON retiré sur ${cible.nodeId} : `
      + `${erreur instanceof Error ? erreur.message : String(erreur)}. Le paragraphe est dans le `
      + 'document mais peut rester invisible à l\'écran (#998).',
    );
  }
}

/**
 * Rend un bloc en HTML pour `insertContentAt`. Utilise le même mapping que
 * `blocksToHtml.ts` (PR1) sans dépendre de la lib pour rester local.
 */
function renderBlockHtml(text: string, styleKey: string): string {
  const escaped = escapeHtml(text);
  const level = HEADING_STYLE_LEVELS[styleKey];
  if (typeof level === 'number') return `<h${level}>${escaped}</h${level}>`;
  return `<p>${escaped}</p>`;
}

/**
 * Échappement d'un NŒUD DE TEXTE — `&`, `<` et `>` seulement.
 *
 * ⚠️ **Les apostrophes et guillemets ne sont PAS échappés, et c'est
 * load-bearing** (#980, mesuré le 2026-08-15). `superdoc@2` ne décode aucune
 * entité à l'insertion HTML : un `&#39;` émis ici atterrit **littéralement**
 * dans le document de l'avocat. Mesuré sur le rendu peint — un lot
 * `insert_paragraph` portant « l'agent » rendait `l&#39;agent`, et la
 * spécification qui cherchait le texte au retour ne le trouvait plus.
 *
 * Dans un nœud de texte, `'` et `"` sont de toute façon inertes : ils ne
 * ferment que des valeurs d'attribut. Cette fonction n'en produit aucune —
 * `renderBlockHtml` n'émet plus d'attribut depuis le retrait de `data-style`.
 *
 * Doctrine identique à `editor/blocksToHtml.ts` (`escapeHtml`), qui avait été
 * corrigée seule : les deux sites sérialisent du texte vers le même canal, ils
 * ne peuvent pas diverger sur ce qu'ils échappent.
 *
 * `&`, `<` et `>` restent échappés parce qu'ils changent la STRUCTURE du
 * fragment. Limite connue qui en découle : eux aussi ressortent en toutes
 * lettres sous V2 (`Durand &amp; Fils` mesuré). C'est le même défaut de canal,
 * mais le remède ne peut pas être de les laisser passer — suivi hors de ce lot.
 */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}


/**
 * Style de paragraphe → `styleId` OOXML posé par `styles.paragraph.setStyle`.
 *
 * ⚠️ CHANGEMENT DE MÉCANIQUE, mesuré (#979, 2026-08-14) : les `Hector-H1..6`
 * passaient par la commande Tiptap `setHeading({level})`, qui bascule le TYPE DE
 * NŒUD. Le Document API n'a pas d'équivalent — sa seule surface de style de
 * paragraphe est `styles.paragraph.setStyle({styleId})`, qui pose un
 * `<w:pStyle>`. Or les six styles de titre EXISTENT dans le gabarit
 * (`scripts/generate-blank-template.ts` : `Hector-H1`, `basedOn: 'Heading1'`…),
 * donc le `styleId` demandé est posé tel quel et la lecture rend le même
 * `styleKey` qu'avant.
 *
 * Ce qui change et qui est assumé : le nœud reste un `paragraph`. Le niveau de
 * plan (volet Navigation de Word) vient désormais du style, plus du type de
 * nœud. Le gabarit le porte (`basedOn: Heading1` hérite l'`outlineLvl`), donc
 * l'export `.docx` reste correct.
 */
function styleIdPourLeCanvas(styleKey: string): string {
  if (typeof HEADING_STYLE_LEVELS[styleKey] === 'number') {
    // Alias minuscules du streaming HTML (`h1`…`h6`) → styleId du gabarit.
    return styleKey.startsWith('Hector-') ? styleKey : `Hector-H${HEADING_STYLE_LEVELS[styleKey]}`;
  }
  return styleKey;
}

/** Vocabulaire admis par `apply_style`, inchangé depuis PR3. */
function styleKeyEstConnu(styleKey: string): boolean {
  return typeof HEADING_STYLE_LEVELS[styleKey] === 'number' || HECTOR_STYLE_IDS_PRESET.has(styleKey);
}

/**
 * L'hôte d'interface du canvas — les deux gestes qui touchent la VUE et non le
 * document. **Les deux ont une surface publique, et les deux l'empruntent**
 * depuis #980/T16 (2026-08-15).
 *
 * ⚠️ **Le TODO(#980) qui vivait ici est LEVÉ, et sa prémisse était fausse.** Il
 * disait « aucun équivalent public » : vrai du seul Document API — `doc.*`
 * décrit le document, et `CommentsApi` exclut nommément les opérations d'état
 * d'interface (`comments.d.ts`, « Excludes UI-state operations ») — mais faux
 * du produit. `superdoc.ui` existe précisément pour ça, elle est publique, et
 * elle porte les deux. Elle vit sur l'**INSTANCE**, pas sur l'éditeur : c'est ce
 * décalage d'un cran qui l'avait fait manquer pendant toute la migration.
 *
 * Mesuré à l'EFFET le 2026-08-15, jamais au seul receipt :
 * `ui.viewport.scrollIntoView` défile réellement (0 → 445 px) et sait **monter
 * une page pas encore peinte**, ce que le repli DOM ne peut pas ;
 * `ui.comments.setActive` fait passer `activeId` de `null` à l'identifiant, et
 * rend `false` sur un identifiant inconnu.
 *
 * Les deux replis d'avant (défilement DOM, `editor.commands` de la 1.x, `null`
 * en V2) restent, pour un hôte qui n'exposerait pas `ui`.
 */
export interface HoteUiCanvas {
  /** `editor.commands` de SuperDoc 1.x — `null` sur un runtime V2. */
  commands?: {
    setActiveComment?: (input: { commentId: string | null }) => unknown;
  } | null;
  /**
   * `superdoc.ui` — la surface d'INTERFACE publique de `superdoc@2`, portée par
   * l'INSTANCE et non par l'éditeur.
   *
   * ⚠️ C'est elle que #979 cherchait au mauvais endroit. Sa conclusion — « ces
   * deux verbes n'ont aucun équivalent public » — était juste pour le **Document
   * API** (`doc.*` décrit le document, pas la vue) et fausse pour le produit :
   * `superdoc.ui` existe précisément pour l'état d'interface, et porte les deux
   * opérations. Mesuré à l'exécution le 2026-08-15, effet observé à chaque fois
   * (défilement réel, `activeId` relu), jamais le seul receipt.
   */
  ui?: {
    viewport?: {
      /**
       * ⚠️ La cible est une adresse de TEXTE, jamais de bloc — mesuré :
       * `{kind:'block', …}` rend `{success:false}` sans rien faire, et le
       * typage public le dit (`target: TextAddress | TextTarget |
       * EntityAddress`, sans `BlockNodeAddress`). Une plage collapsée
       * `{start:0,end:0}` suffit, y compris sur un paragraphe VIDE.
       */
      scrollIntoView?: (input: {
        target: { kind: 'text'; blockId: string; range: { start: number; end: number } };
        block?: 'start' | 'center' | 'end' | 'nearest';
        behavior?: 'auto' | 'smooth';
      }) => unknown;
    };
    comments?: {
      /**
       * Rend `true` quand l'activation est acceptée, `false` quand aucun éditeur
       * n'est monté ou qu'aucun commentaire ne porte cet identifiant. `null`
       * efface et est toujours accepté (mesuré : `activeId` passe bien de `null`
       * à l'identifiant, puis revient à `null`).
       */
      setActive?: (commentId: string | null) => unknown;
    };
  };
}

/**
 * Applique un batch d'actions sur le document. Renvoie un résultat par action
 * (success/error). Voir doc `sortActions` pour la sémantique d'ordre.
 *
 * ⚠️ ASYNCHRONE depuis #979 : chaque opération du Document API est attendue.
 * Le lot entier reste sérialisé par le runtime, mais chaque mutation ouvre sa
 * propre fenêtre de mode suivi/direct ; les lectures intermédiaires restent hors
 * de cette fenêtre. SuperDoc 2 ne change aucun auteur. Voir `WebSocketPool`.
 *
 * Les blocs adressables sont relus AVANT CHAQUE action, jamais mis en cache : une
 * insertion ou une suppression décale les index suivants, et c'est précisément
 * ce sur quoi `sortActions` compte. C'était déjà le comportement du walk
 * ProseMirror, qui relisait `editor.state.doc` à chaque tour.
 */
export async function applyActions(
  ctx: CanvasBridgeContext,
  actions: CanvasAction[],
  hoteUi?: HoteUiCanvas,
): Promise<CanvasActionResult[]> {
  const doc = ctx.doc;
  const sorted = sortActions(actions);
  const results: CanvasActionResult[] = [];

  for (const action of sorted) {
    try {
      switch (action.kind) {
        case 'insert_paragraph': {
          const { blocs, revision } = await lireLesBlocsEtLaRevision(doc);
          const fence = ouvrirLaFence(ctx, revision);
          const html = renderBlockHtml(action.text, action.style);
          if (typeof doc.insert !== 'function') {
            throw new Error('insert_paragraph: `doc.insert` indisponible sur cet éditeur SuperDoc.');
          }
          // -1 = insertion en tête. Au-delà du dernier index, on vise le
          // DERNIER paragraphe adressable avec `after` — surtout pas l'insert
          // sans locator : mesuré sur le vrai SuperDoc (2026-08-14), un insert
          // HTML sans cible ajoute un paragraphe VIDE avant le contenu demandé.
          // Viser le dernier bloc reproduit exactement l'ancien clamp de
          // `paragraphInsertPosition` (« après le dernier paragraphe »), sans
          // ce paragraphe parasite. L'insert sans locator ne subsiste que pour
          // un document sans aucun bloc adressable, où il n'y a rien à viser.
          const cible = action.after_index < 0
            ? blocs[0]
            : (blocs[action.after_index] ?? blocs.at(-1));
          const placement = action.after_index < 0 ? 'before' : 'after';
          const receiptInsertion = await muterEtExigerSucces(fence, 'insert_paragraph', 'doc.insert', (options) =>
            doc.insert!(
              cible === undefined
                ? { value: html, type: 'html' }
                : { target: adresseDe(cible), placement, value: html, type: 'html' },
              options,
            ));
          // Le provider recopie les propriétés du voisin sur le bloc inséré, saut
          // de page compris — et un paragraphe qui ouvre sa propre page en fin de
          // document n'est jamais peint (#998, mesuré). Voir `retirerLeSautHerite`.
          await retirerLeSautHerite(fence, doc, receiptInsertion);
          results.push({ action, success: true });
          break;
        }

        case 'delete_paragraph': {
          const lu = await lireLesBlocsEtLaRevision(doc);
          const fence = ouvrirLaFence(ctx, lu.revision);
          const bloc = resoudreParagraphe(lu.blocs, action.index);
          if (typeof doc.blocks?.delete !== 'function') {
            throw new Error('delete_paragraph: `doc.blocks.delete` indisponible sur cet éditeur SuperDoc.');
          }
          await muterEtExigerSucces(fence, 'delete_paragraph', 'doc.blocks.delete', (options) =>
            doc.blocks!.delete!({ target: adresseDe(bloc) }, options));
          results.push({ action, success: true });
          break;
        }

        case 'replace_paragraph': {
          const lu = await lireLesBlocsEtLaRevision(doc);
          const fence = ouvrirLaFence(ctx, lu.revision);
          const bloc = resoudreParagraphe(lu.blocs, action.index);
          if (typeof doc.insert !== 'function' || typeof doc.blocks?.delete !== 'function') {
            throw new Error('replace_paragraph: `doc.insert` et `doc.blocks.delete` sont requis sur cet éditeur SuperDoc.');
          }
          // Le style suit celui du bloc remplacé quand l'agent n'en demande pas
          // — et un titre remplacé garde SON NIVEAU. Le repli sur H1 aurait
          // rétrogradé tout H2..H6, cassant la hiérarchie du volet Navigation de
          // Word ; le chemin Tiptap d'avant, lui, restituait le niveau réel.
          // `headingLevel` est projeté par `listerBlocsAdressables` depuis
          // `BlockListEntry`. Un titre sans niveau lisible retombe sur H1, faute
          // de mieux — mais c'est alors le provider qui n'a pas su le dire.
          const styleKey = action.style
            ?? (bloc.nodeType === 'heading' ? `Hector-H${bloc.headingLevel ?? 1}` : 'Hector-Normal');
          const html = renderBlockHtml(action.text, styleKey);
          // DEUX opérations, dans cet ordre : insérer d'abord (la cible existe
          // encore et sert d'ancre), supprimer ensuite. L'inverse perdrait
          // l'ancre et renverrait le texte en fin de document — c'est
          // exactement le défaut que le chemin Tiptap produisait (mesuré
          // 2026-08-14 : `insertContentAt({from,to})` vidait le paragraphe cible
          // et posait le nouveau texte en QUEUE du document).
          await muterEtExigerSucces(fence, 'replace_paragraph (insertion)', 'doc.insert', (options) =>
            doc.insert!({ target: adresseDe(bloc), placement: 'after', value: html, type: 'html' }, options));
          await muterEtExigerSucces(fence, 'replace_paragraph (suppression de l\'ancien)', 'doc.blocks.delete', (options) =>
            doc.blocks!.delete!({ target: adresseDe(bloc) }, options));
          results.push({ action, success: true });
          break;
        }

        case 'apply_style': {
          if (!styleKeyEstConnu(action.style)) {
            results.push({
              action,
              success: false,
              error:
                `styleKey inconnu: '${action.style}'. Accepted: Hector-H1..H6 | ` +
                Array.from(HECTOR_STYLE_IDS_PRESET).join(' | ') + '.',
            });
            break;
          }
          const lu = await lireLesBlocsEtLaRevision(doc);
          const fence = ouvrirLaFence(ctx, lu.revision);
          const bloc = resoudreParagraphe(lu.blocs, action.index);
          if (typeof doc.styles?.paragraph?.setStyle !== 'function') {
            throw new Error('apply_style: `doc.styles.paragraph.setStyle` indisponible sur cet éditeur SuperDoc.');
          }
          await muterEtExigerSucces(fence, 'apply_style', 'doc.styles.paragraph.setStyle', (options) =>
            doc.styles!.paragraph!.setStyle!(
              { target: adresseDe(bloc), styleId: styleIdPourLeCanvas(action.style) },
              options,
            ));
          results.push({ action, success: true });
          break;
        }

        case 'search_replace': {
          const lu = await lireLesBlocsEtLaRevision(doc);
          const fence = ouvrirLaFence(ctx, lu.revision);
          const bloc = resoudreParagraphe(lu.blocs, action.paragraph_index);
          const occurrences = await trouverLesOccurrences(doc, bloc, action.search_text, { caseSensitive: true });
          if (occurrences.length === 0) {
            results.push({ action, success: false, error: `Texte introuvable dans paragraph_index=${action.paragraph_index}.` });
            break;
          }
          if (typeof doc.replace !== 'function') {
            throw new Error('search_replace: `doc.replace` indisponible sur cet éditeur SuperDoc.');
          }
          // Première occurrence seulement — parité avec le comportement d'avant.
          await muterEtExigerSucces(fence, 'search_replace', 'doc.replace', (options) =>
            doc.replace!({ target: occurrences[0]!.target, text: action.replace_text }, options));
          results.push({ action, success: true });
          break;
        }

        case 'apply_text_format': {
          if (!action.format.bold && !action.format.italic && !action.format.underline) {
            results.push({ action, success: false, error: 'apply_text_format requiert au moins un de bold/italic/underline.' });
            break;
          }
          const lu = await lireLesBlocsEtLaRevision(doc);
          const fence = ouvrirLaFence(ctx, lu.revision);
          const bloc = resoudreParagraphe(lu.blocs, action.paragraph_index);
          const occurrences = await trouverLesOccurrences(doc, bloc, action.search_text);
          if (occurrences.length === 0) {
            results.push({ action, success: false, error: `Texte introuvable (case-insensitive) dans paragraph_index=${action.paragraph_index}.` });
            break;
          }
          if (typeof doc.format?.apply !== 'function') {
            throw new Error('apply_text_format: `doc.format.apply` indisponible sur cet éditeur SuperDoc.');
          }
          // ⚠️ L'ORDRE EST LOAD-BEARING, et il a été MESURÉ (2026-08-14) :
          // souligné → italique → gras. Dans l'ordre inverse (gras d'abord),
          // seul le souligné survit sur le vrai SuperDoc — les deux premières
          // marques disparaissent en silence, chaque appel rendant pourtant
          // `success:true`. Le même patch posé d'un seul bloc
          // (`{bold, italic, underline}`) produit exactement le même effet
          // tronqué. Isolément, chaque marque se pose correctement : ce n'est
          // donc ni le patch ni la cible qui sont en cause, mais l'ordre de pose
          // sur une même plage. À re-sonder au bump (#980).
          const marques = [
            ...(action.format.underline ? [{ underline: true }] : []),
            ...(action.format.italic ? [{ italic: true }] : []),
            ...(action.format.bold ? [{ bold: true }] : []),
          ];
          await poserLesMarques(fence, bloc, action.search_text, occurrences.length, marques, 'apply_text_format');
          results.push({ action, success: true });
          break;
        }

        case 'highlight_range': {
          const lu = await lireLesBlocsEtLaRevision(doc);
          const fence = ouvrirLaFence(ctx, lu.revision);
          const bloc = resoudreParagraphe(lu.blocs, action.paragraph_index);
          const occurrences = await trouverLesOccurrences(doc, bloc, action.search_text);
          if (occurrences.length === 0) {
            results.push({ action, success: false, error: `Texte introuvable (case-insensitive) dans paragraph_index=${action.paragraph_index}.` });
            break;
          }
          if (typeof doc.format?.apply !== 'function') {
            throw new Error('highlight_range: `doc.format.apply` indisponible sur cet éditeur SuperDoc.');
          }
          const colorKey = action.color && action.color in HIGHLIGHT_OOXML ? action.color : 'yellow';
          await poserLesMarques(
            fence, bloc, action.search_text, occurrences.length,
            [{ highlight: HIGHLIGHT_OOXML[colorKey]! }], 'highlight_range',
          );
          results.push({ action, success: true });
          break;
        }

        case 'apply_font': {
          if (!action.font_name && typeof action.font_size !== 'number') {
            results.push({ action, success: false, error: 'apply_font requiert font_name ou font_size.' });
            break;
          }
          const lu = await lireLesBlocsEtLaRevision(doc);
          const fence = ouvrirLaFence(ctx, lu.revision);
          const bloc = resoudreParagraphe(lu.blocs, action.paragraph_index);
          const ref = exigerRefDeBloc('apply_font', bloc);
          if (typeof doc.format?.apply !== 'function') {
            throw new Error('apply_font: `doc.format.apply` indisponible sur cet éditeur SuperDoc.');
          }
          // Sémantique inchangée depuis PR2 : la police se pose au niveau du RUN
          // (aligné OOXML `w:rPr`), sur tout le texte du bloc — d'où le `ref` du
          // bloc entier plutôt qu'une adresse de nœud.
          await muterEtExigerSucces(fence, 'apply_font', 'doc.format.apply', (options) =>
            doc.format!.apply!({
              ref,
              inline: {
                ...(action.font_name ? { fontFamily: action.font_name } : {}),
                ...(typeof action.font_size === 'number' ? { fontSize: action.font_size } : {}),
              },
            }, options));
          results.push({ action, success: true });
          break;
        }

        case 'set_alignment': {
          if (!ALIGN_VALUES.has(action.alignment)) {
            results.push({ action, success: false, error: `alignment invalide : ${action.alignment}.` });
            break;
          }
          const lu = await lireLesBlocsEtLaRevision(doc);
          const fence = ouvrirLaFence(ctx, lu.revision);
          const bloc = resoudreParagraphe(lu.blocs, action.paragraph_index);
          if (typeof doc.format?.paragraph?.setAlignment !== 'function') {
            throw new Error('set_alignment: `doc.format.paragraph.setAlignment` indisponible sur cet éditeur SuperDoc.');
          }
          await muterEtExigerSucces(fence, 'set_alignment', 'doc.format.paragraph.setAlignment', (options) =>
            doc.format!.paragraph!.setAlignment!(
              { target: adresseDe(bloc), alignment: action.alignment },
              options,
            ));
          results.push({ action, success: true });
          break;
        }

        case 'clear_formatting': {
          const index = action.paragraph_index;
          // Le premier tour valide l'index et l'existence d'un ref ; les
          // suivants RELISENT, parce que chaque mutation périme le précédent.
          const luInitial = await lireLesBlocsEtLaRevision(doc);
          const fence = ouvrirLaFence(ctx, luInitial.revision);
          resoudreParagraphe(luInitial.blocs, index);
          if (typeof doc.format?.apply !== 'function') {
            throw new Error('clear_formatting: `doc.format.apply` indisponible sur cet éditeur SuperDoc.');
          }
          // `null` = RETIRER la propriété (`InlineRunPatch` : `boolean | null`).
          // Le node type n'est pas touché : un titre reste un titre, comme avant.
          for (const inline of PROPRIETES_A_RETIRER) {
            const frais = resoudreParagraphe(await listerBlocsAdressables(doc), index);
            const ref = exigerRefDeBloc('clear_formatting', frais);
            await muterEtExigerSucces(fence, 'clear_formatting', 'doc.format.apply', (options) =>
              doc.format!.apply!({ ref, inline }, options));
          }
          results.push({ action, success: true });
          break;
        }

        case 'insert_comment': {
          if (!action.comment_text || action.comment_text.trim() === '') {
            results.push({ action, success: false, error: 'insert_comment requiert comment_text non vide.' });
            break;
          }
          const lu = await lireLesBlocsEtLaRevision(doc);
          const fence = ouvrirLaFence(ctx, lu.revision);
          const bloc = resoudreParagraphe(lu.blocs, action.paragraph_index);
          const occurrences = await trouverLesOccurrences(doc, bloc, action.search_text);
          if (occurrences.length === 0) {
            results.push({ action, success: false, error: `Texte introuvable dans paragraph_index=${action.paragraph_index} pour insert_comment.` });
            break;
          }
          if (typeof doc.comments?.create !== 'function') {
            throw new Error('insert_comment: `doc.comments.create` indisponible sur cet éditeur SuperDoc (superdoc 1.43+ requis).');
          }
          // ⚠️ `comments.create` veut une ADRESSE DE TEXTE (`{kind:'text',
          // blockId, range}`), pas la cible de sélection que prennent `replace`
          // et `format.apply` — les deux formes sortent du même item de
          // `query.match`, il ne faut pas les confondre.
          for (const occurrence of occurrences) {
            // `comments.create` prend des `RevisionGuardOptions`, PAS des
            // `MutationOptions` : la révision seule, aucun `changeMode` (vérifié
            // dans `comments.d.ts`). Passer un mode ici serait un champ inconnu.
            await muterEtExigerSucces(fence, 'insert_comment', 'doc.comments.create', ({ expectedRevision }) =>
              doc.comments!.create!({
                text: action.comment_text,
                target: { kind: 'text', blockId: occurrence.blockId, range: occurrence.range },
              }, { expectedRevision }));
          }
          results.push({ action, success: true });
          break;
        }

        case 'navigate_to_paragraph': {
          // ✅ #980 (T16) — le TODO est levé. `scrollIntoView` vit bien sous
          // `ui.viewport`, comme #979 le supposait : ce qui manquait, c'est que
          // cette surface est PUBLIQUE et portée par l'instance SuperDoc.
          // Aucune fence ici : ce verbe ne mute pas le document, il fait
          // défiler la vue. Il n'y a donc rien à protéger d'une frappe.
          const bloc = resoudreParagraphe(await listerBlocsAdressables(doc), action.paragraph_index);
          const refus = await faireDefilerVersLeBloc(bloc, hoteUi, ctx.queryRoot);
          results.push(refus === undefined
            ? { action, success: true }
            : { action, success: false, error: refus });
          break;
        }

        // ─── operations sur commentaires existants ────────────────────────
        // Document API depuis 2026-07-03. L'adressage par commentId est fiable
        // (contrairement à l'adressage par blockId, cf. CLAUDE.md du module) et
        // vérifié en live : patch({status:'resolved'|'active'}) + delete rendent
        // des receipts {success, failure{code,message}} → erreurs LLM-lisibles.
        case 'resolve_comment':
        case 'reopen_comment': {
          const statut = action.kind === 'resolve_comment' ? 'resolved' : 'active';
          if (typeof doc.comments?.patch !== 'function') {
            throw new Error(`${action.kind}: \`doc.comments.patch\` indisponible sur cet éditeur SuperDoc (superdoc 1.43+ requis).`);
          }
          const r = await muterSousFences(
            ctx, `${action.kind}:doc.comments.patch`,
            { changeMode: 'direct', expectedRevision: await revisionCourante(ctx, ctx.expectedRevision ?? '') },
            () => doc.comments!.patch!({ commentId: action.comment_id, status: statut }),
          );
          const echec = echecDuProvider(r);
          results.push(echec === undefined
            ? { action, success: true }
            : { action, success: false, error: `${action.kind} a échoué pour commentId='${action.comment_id}' — ${echec}.` });
          break;
        }

        case 'delete_comment': {
          if (typeof doc.comments?.delete !== 'function') {
            throw new Error('delete_comment: `doc.comments.delete` indisponible sur cet éditeur SuperDoc (superdoc 1.43+ requis).');
          }
          const r = await muterSousFences(
            ctx, 'delete_comment:doc.comments.delete',
            { changeMode: 'direct', expectedRevision: await revisionCourante(ctx, ctx.expectedRevision ?? '') },
            () => doc.comments!.delete!({ commentId: action.comment_id }),
          );
          const echec = echecDuProvider(r);
          results.push(echec === undefined
            ? { action, success: true }
            : { action, success: false, error: `delete_comment a échoué pour commentId='${action.comment_id}' — ${echec}.` });
          break;
        }

        case 'set_active_comment': {
          // ✅ #980 (T16) — le TODO est levé. `doc.comments` exclut bien les
          // opérations d'état d'interface (doctrine upstream, `comments.d.ts`),
          // mais elles vivent sur `superdoc.ui.comments`, qui EST publique.
          // Mesuré : activation d'un commentaire réel → `true` et `activeId`
          // passe de `null` à son identifiant ; identifiant inconnu → `false` ;
          // `null` → `true` et `activeId` revient à `null`.
          const surfaceUi = hoteUi?.ui?.comments;
          const commandes = hoteUi?.commands;
          // ⚠️ Le repli V1 est COURT-CIRCUITÉ dès que `ui` existe, délibérément :
          // un `false` de la surface V2 est un refus MOTIVÉ (identifiant
          // inconnu, aucun éditeur monté), pas une panne à contourner — et sur
          // un runtime V2 `editor.commands` vaut `null` de toute façon.
          // Enchaîner les deux chemins ferait croire à une redondance qui
          // n'existe pas, et masquerait le motif du refus.
          const ok = typeof surfaceUi?.setActive === 'function'
            ? surfaceUi.setActive(action.comment_id) === true
            // Repli V1 : `editor.commands` vaut `null` en V2, donc ce chemin ne
            // sert plus que si le canvas tourne encore sur un runtime 1.x.
            : typeof commandes?.setActiveComment === 'function'
              && commandes.setActiveComment({ commentId: action.comment_id }) === true;
          results.push({
            action,
            success: ok,
            ...(ok ? {} : {
              error: `set_active_comment a échoué pour commentId='${action.comment_id ?? 'null'}' — `
                + 'aucun commentaire ne porte cet identifiant, ou aucun éditeur n\'est monté. '
                + 'Relire la liste des commentaires avant de réessayer.',
            }),
          });
          break;
        }

        default: {
          // Exhaustivité TS — si CanvasAction gagne un nouveau kind sans
          // mise à jour ici, le compilateur le signale via never.
          const _exhaustive: never = action;
          void _exhaustive;
          results.push({ action, success: false, error: 'unknown action kind' });
        }
      }
    } catch (err) {
      results.push({ action, success: false, error: (err as Error).message });
    }
  }

  return results;
}

/**
 * Le `ref` d'un bloc — handle de mutation sur son texte entier.
 *
 * `format.apply` n'accepte qu'une sélection ou un ref, jamais une adresse de
 * bloc (1.44) : sans ref, la mise en forme d'un paragraphe entier n'a pas de
 * cible. Un bloc VIDE n'en porte pas (`BlockListEntry` : « A block whose content
 * is entirely tracked-deleted reads as "" and reports isEmpty: true with no
 * ref ») — le refus nomme donc ce cas plutôt que de laisser passer un
 * `ref: undefined` que le provider rejetterait sans expliquer pourquoi.
 */
function exigerRefDeBloc(operation: string, bloc: BlocAdressable): string {
  if (bloc.ref === undefined) {
    throw new Error(
      `${operation}: le paragraphe ${bloc.index} n'expose aucun ref de mutation — il est vide `
      + '(un bloc sans texte visible n\'a pas de cible de mise en forme).',
    );
  }
  return bloc.ref;
}

/**
 * Fait défiler jusqu'au bloc. Rend `undefined` en succès, le message de refus
 * sinon.
 *
 * ⚠️ **DEUX chemins, et le premier est public depuis #980 (T16)** :
 * `superdoc.ui.viewport.scrollIntoView` monte la page cible si elle n'est pas
 * encore peinte ; le repli DOM ci-dessous ne trouve que ce qui est déjà rendu.
 * Les attributs DOM restent documentés parce que le repli les utilise encore,
 * et parce que la mesure qui les a établis vaut toujours.
 *
 * ⚠️ **Les deux attributs d'identité ont été MESURÉS** dans le canvas réel
 * (2026-08-14) — l'un d'eux avait été inventé et ne matchait jamais, donc le
 * verbe annonçait un succès sans rien faire :
 *
 * | attribut | où | verdict |
 * |---|---|---|
 * | `data-node-id` | nulle part | **n'existe pas** |
 * | `data-block-id` | `superdoc-fragment` de la page PEINTE | ✅ visé en premier |
 * | `data-sd-block-id` | `<p>` de l'éditeur ProseMirror | repli |
 *
 * L'ordre est load-bearing : l'éditeur ProseMirror est **parqué hors écran**
 * (#856), donc y faire défiler ne montrerait rien à l'avocat. C'est la page
 * peinte qu'il regarde.
 *
 * Le curseur n'est plus posé : le Document API n'a pas de setter de sélection,
 * et le chemin Tiptap qui le faisait meurt avec `editor.commands` en V2. Le
 * verbe ne promet donc plus que le défilement — et le DIT quand il ne peut même
 * pas ça, au lieu d'annoncer un succès.
 */
async function faireDefilerVersLeBloc(
  bloc: BlocAdressable,
  hoteUi?: HoteUiCanvas,
  queryRoot?: ParentNode,
): Promise<string | undefined> {
  // ─── Chemin PUBLIC, préféré (#980 T16) ───────────────────────────────────
  // `superdoc.ui.viewport.scrollIntoView` sait **monter la page** si elle n'est
  // pas encore peinte (« Handles paginated, virtualized layouts by mounting the
  // target page », typages publics) — ce que le défilement DOM ne peut pas
  // faire : il ne trouve que ce qui est déjà rendu, et un acte long ne l'est
  // jamais entièrement.
  //
  // ⚠️ La cible est une adresse de TEXTE, plage collapsée comprise — mesuré :
  // `{kind:'block'}` rend `{success:false}` sans rien faire, `{kind:'text',
  // blockId, range:{start:0,end:0}}` défile, y compris vers un paragraphe VIDE.
  const scrollIntoView = hoteUi?.ui?.viewport?.scrollIntoView;
  if (typeof scrollIntoView === 'function') {
    const receipt = await scrollIntoView({
      target: { kind: 'text', blockId: bloc.nodeId, range: { start: 0, end: 0 } },
      block: 'center',
      behavior: 'smooth',
    });
    if (estUnObjet(receipt) && receipt.success === false) {
      return `navigate_to_paragraph: le paragraphe ${bloc.index} (nodeId=${bloc.nodeId}) n'a pas pu être `
        + 'atteint — la surface de défilement de SuperDoc n\'a pas résolu sa cible. Le document vient '
        + 'peut-être d\'être remplacé.';
    }
    return undefined;
  }

  // ─── Repli DOM, pour un hôte sans surface d'interface ────────────────────
  if (!queryRoot) {
    return `navigate_to_paragraph: aucun document n'est rendu dans cet hôte — le défilement vers le `
      + `paragraphe ${bloc.index} (nodeId=${bloc.nodeId}) n'a pas de cible.`;
  }
  const element = queryRoot.querySelector<HTMLElement>(`[data-block-id="${bloc.nodeId}"]`)
    ?? queryRoot.querySelector<HTMLElement>(`[data-sd-block-id="${bloc.nodeId}"]`);
  if (!element || typeof element.scrollIntoView !== 'function') {
    return `navigate_to_paragraph: le paragraphe ${bloc.index} (nodeId=${bloc.nodeId}) n'a pas de nœud `
      + 'rendu dans le document affiché — rien n\'a défilé. Le document vient peut-être d\'être '
      + 'remplacé, ou la page qui le porte n\'est pas encore peinte.';
  }
  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  return undefined;
}
